// Photo preparation before POST /api/web/upload, shared by the corrido flow
// and the FaceLock dialogs.
//
// - normalizeCorridoPhotoForUpload: HEIC/HEIF (and anything else the server
//   cannot read) becomes a JPEG; JPEG/PNG/WebP upload untouched. This is the
//   corrido behaviour, unchanged since it moved out of CorridoChatFlow.
// - preparePhotoForUpload(file, { maxEdge }): the same, plus a downscale so the
//   longest edge is at most `maxEdge` px (a JPEG). A photo already small enough
//   in a safe format is uploaded as-is. FaceLock uses 1600 px: 24 phone photos
//   at 4000 px would be a 100 MB upload for pixels the trainer never uses.

export const PHOTO_UPLOAD_ACCEPT = "image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif";
/** Kept for the corrido flow's file input. */
export const CORRIDO_UPLOAD_ACCEPT = PHOTO_UPLOAD_ACCEPT;
/** The longest edge of a FaceLock photo after preparation. */
export const FACELOCK_MAX_EDGE = 1600;

const SAFE_UPLOAD_MIME_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"]);
const ACCEPTED_MIME_TYPES = new Set([...SAFE_UPLOAD_MIME_TYPES, "image/heic", "image/heif"]);
const ACCEPTED_EXTENSIONS = new Set(["jpg", "jpeg", "png", "webp", "heic", "heif"]);
const JPEG_QUALITY = 0.92;
/** A decode that never answers must not freeze an upload forever. */
const DECODE_TIMEOUT_MS = 20000;

function fileExtension(name: string): string {
  const match = /\.([^.]+)$/.exec(String(name || "").toLowerCase());
  return match?.[1] || "";
}

/** A photo type the page accepts (HEIC included: it is converted). */
export function isAcceptedPhoto(file: File): boolean {
  const mime = String(file.type || "").toLowerCase();
  if (ACCEPTED_MIME_TYPES.has(mime)) return true;
  return ACCEPTED_EXTENSIONS.has(fileExtension(file.name));
}
export const isAcceptedCorridoPhoto = isAcceptedPhoto;

/** JPEG, PNG and WebP go to the server as they are. */
export function canUploadPhotoWithoutConversion(file: File): boolean {
  const mime = String(file.type || "").toLowerCase();
  if (SAFE_UPLOAD_MIME_TYPES.has(mime)) return true;
  return ["jpg", "jpeg", "png", "webp"].includes(fileExtension(file.name));
}

async function loadImageElement(file: File): Promise<HTMLImageElement> {
  const objectUrl = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const next = new Image();
      const timer = setTimeout(() => reject(new Error("image_decode_timeout")), DECODE_TIMEOUT_MS);
      next.onload = () => { clearTimeout(timer); resolve(next); };
      next.onerror = () => { clearTimeout(timer); reject(new Error("image_decode_failed")); };
      next.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/** The size a `width` x `height` picture gets when its longest edge is capped. */
export function fitWithin(width: number, height: number, maxEdge?: number): { width: number; height: number } {
  if (!maxEdge || maxEdge <= 0) return { width, height };
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const scale = maxEdge / longest;
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

async function encodeJpeg(file: File, img: HTMLImageElement, width: number, height: number): Promise<File> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("image_conversion_unavailable");
  ctx.drawImage(img, 0, 0, width, height);
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("image_encode_failed"));
    }, "image/jpeg", JPEG_QUALITY);
  });
  const base = String(file.name || "photo").replace(/\.[^.]+$/, "") || "photo";
  return new File([blob], `${base}.jpg`, { type: "image/jpeg", lastModified: file.lastModified || Date.now() });
}

/** Convert when the server cannot read the type; optionally cap the longest edge. */
export async function preparePhotoForUpload(file: File, opts: { maxEdge?: number } = {}): Promise<File> {
  const safe = canUploadPhotoWithoutConversion(file);
  if (safe && !opts.maxEdge) return file;
  let img: HTMLImageElement;
  try {
    img = await loadImageElement(file);
  } catch (e) {
    // A safe type the browser could not decode for resizing still uploads
    // as it is; the server resizes too. Only a conversion must succeed.
    if (safe) return file;
    throw e;
  }
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (!width || !height) {
    if (safe) return file;
    throw new Error("image_decode_failed");
  }
  const target = fitWithin(width, height, opts.maxEdge);
  if (safe && target.width === width && target.height === height) return file;
  try {
    return await encodeJpeg(file, img, target.width, target.height);
  } catch (e) {
    if (safe) return file;
    throw e;
  }
}

/** The corrido flow's preparation: conversion only, never a resize. */
export async function normalizeCorridoPhotoForUpload(file: File): Promise<File> {
  return preparePhotoForUpload(file);
}
